import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Banknote, CalendarCheck, Globe, Zap, Check, PlayCircle, ArrowRight, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import CodeBlock from "@/components/CodeBlock";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

const ICON_MAP = { banknote: Banknote, "calendar-check": CalendarCheck, globe: Globe, zap: Zap };

function buildSnippet(product, language) {
  const base = "https://loftpress.shop";
  const path = (product.endpoints[0]?.path || "/").replace(/{[^}]+}/g, "2026");
  const url = `${base}${product.base_path}${path}`;
  if (language === "cURL") {
    return `curl -H "X-API-Key: $LOFTPRESS_KEY" \\\n  "${url}"`;
  }
  if (language === "JavaScript") {
    return `const res = await fetch("${url}", {\n  headers: { "X-API-Key": process.env.LOFTPRESS_KEY }\n});\nconst data = await res.json();\nconsole.log(data);`;
  }
  if (language === "Python") {
    return `import requests\n\nres = requests.get(\n  "${url}",\n  headers={"X-API-Key": os.environ["LOFTPRESS_KEY"]},\n)\nprint(res.json())`;
  }
  if (language === "PHP") {
    return `<?php\n$ch = curl_init("${url}");\ncurl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-Key: " . getenv("LOFTPRESS_KEY")]);\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\n$response = curl_exec($ch);\ncurl_close($ch);\necho $response;`;
  }
  return "";
}

export default function ApiDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = React.useState(null);
  const [snippetLang, setSnippetLang] = React.useState("cURL");
  const [pgLoading, setPgLoading] = React.useState(false);
  const [pgResponse, setPgResponse] = React.useState(null);
  const [subLoading, setSubLoading] = React.useState(false);
  const [pendingPlan, setPendingPlan] = React.useState(null);
  const [consentTerms, setConsentTerms] = React.useState(false);
  const [consentPrivacy, setConsentPrivacy] = React.useState(false);
  const { user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();

  React.useEffect(() => {
    api.get(`/api/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) {
    return (
      <div>
        <Header />
      <Seo title={product ? ((lang==="tr"&&product.name_tr)||product.name) : "API"} description={product ? ((lang==="tr"&&product.description_tr)||product.description) : ""} path={`/products/${slug}`} jsonLd={product && {"@context":"https://schema.org","@type":"SoftwareApplication","name":product.name,"description":product.description,"applicationCategory":product.category,"offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}}} />
        <div className="max-w-7xl mx-auto px-6 py-24"><div className="h-64 lp-shimmer rounded-2xl" /></div>
      </div>
    );
  }
  if (product === false) {
    return (
      <div>
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="font-display text-2xl text-white/80">{t("api.detail.not_found")}</div>
          <Link to="/marketplace" className="lp-btn-primary mt-6 inline-flex">{t("api.detail.back")}</Link>
        </div>
      </div>
    );
  }

  const Icon = ICON_MAP[product.icon] || Zap;

  const runPlayground = async () => {
    if (product.coming_soon) {
      toast.info(t("api.detail.playground_coming_soon"));
      setPgResponse({ status: "coming_soon", message: t("api.detail.playground_coming_soon") });
      return;
    }
    setPgLoading(true);
    setPgResponse(null);
    try {
      let url = "";
      if (slug === "currency") url = "/api/playground/currency/latest?base=USD";
      else if (slug === "holidays") url = "/api/playground/holidays/public/US/2026";
      else if (slug === "ip-intelligence") url = "/api/playground/ip/self";
      else if (slug === "vat") url = "/api/playground/vat/calculate?amount=100&country=DE&rate=standard";
      else if (slug === "email") url = "/api/playground/email/validate?email=support@loftpress.shop";
      else if (slug === "iban") url = "/api/playground/iban/validate?iban=DE89370400440532013000";
      else if (slug === "qr") url = "/api/playground/qr/generate?data=https://loftpress.shop&size=200&format=svg";
      else if (slug === "phone") url = "/api/playground/phone/validate?number=%2B905551234567&region=TR";
      else if (slug === "geo") url = "/api/playground/geo/countries";
      else if (slug === "timezone") url = "/api/playground/timezone/now?tz=Europe/Istanbul";
      else if (slug === "text") url = "/api/playground/text/slugify?text=Merhaba%20D%C3%BCnya";
      else url = "/api/metrics/public";
      const { data } = await api.get(url);
      setPgResponse(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns { code: "coming_soon", message: "..." } for not-yet-live products
      if (detail && typeof detail === "object" && detail.code === "coming_soon") {
        setPgResponse({ status: "coming_soon", message: detail.message || t("api.detail.playground_coming_soon") });
      } else {
        setPgResponse({ error: formatApiErrorDetail(detail) || e.message });
      }
    }
    setPgLoading(false);
  };

  const subscribe = async (plan) => {
    if (!user || user === false) {
      toast.error("Please sign in first");
      nav("/login");
      return;
    }
    // Contact-sales plans (custom-priced) still route to the contact form.
    // Enterprise is a normal paid recurring plan ($500/mo) and MUST use the
    // same Creem checkout path as Pro — never route it to /legal/contact.
    if (plan.contact_sales) {
      nav("/legal/contact");
      return;
    }
    if (product.coming_soon) {
      toast.error(t("api.detail.coming_soon_toast"));
      return;
    }
    if (!product.sale_ready) {
      toast.error(t("api.detail.pending_toast"));
      return;
    }
    setPendingPlan(plan);
  };

  const confirmSubscribe = async () => {
    if (!pendingPlan) return;
    if (!consentTerms || !consentPrivacy) {
      toast.error(t("checkout.consent.required"));
      return;
    }
    setSubLoading(true);
    try {
      const { data } = await api.post("/api/creem/checkout/subscription", {
        product_id: product.id, plan_id: pendingPlan.id,
        language: localStorage.getItem("lp_lang") || "en",
        accept_terms: consentTerms,
        accept_privacy: consentPrivacy,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Checkout URL missing from Creem response");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setSubLoading(false);
  };

  const snippet = buildSnippet(product, snippetLang);

  return (
    <div>
      <Header />
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-center gap-2 text-sm text-white/50 mb-6">
          <Link to="/marketplace" className="hover:text-white">{t("api.breadcrumb.marketplace")}</Link>
          <span>/</span>
          <span className="text-white/80">{displayProductName((lang === "tr" && product.name_tr) || product.name)}</span>
        </div>
        <div className="grid lg:grid-cols-[2fr_1fr] gap-10 items-start">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <Icon className="w-7 h-7 text-[#d4af37]" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="lp-chip">{product.category}</span>
                  {product.coming_soon ? (
                    <span className="lp-chip gold" data-testid="api-detail-coming-soon">{t("marketplace.coming_soon")}</span>
                  ) : (
                    <>
                      <span className={`lp-chip ${product.maintenance ? "amber" : "green"}`}>
                        {product.maintenance ? t("api.detail.maintenance") : t("api.detail.operational")}
                      </span>
                      {!product.sale_ready && <span className="lp-chip amber">{t("marketplace.pending_config")}</span>}
                    </>
                  )}
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-white/95 mt-2">{displayProductName((lang === "tr" && product.name_tr) || product.name)}</h1>
              </div>
            </div>
            <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-2xl">{(lang === "tr" && product.description_tr) || product.description}</p>

            {product.coming_soon && (
              <div className="mt-6 lp-glass p-4 border-l-4 border-[#d4af37]/70 text-sm" data-testid="coming-soon-banner">
                <div className="text-[#d4af37] font-medium">{t("api.detail.coming_soon_title")}</div>
                <div className="text-white/70 mt-1">{t("api.detail.coming_soon_body")}</div>
              </div>
            )}
            {!product.coming_soon && !product.sale_ready && (
              <div className="mt-6 lp-glass p-4 border-l-4 border-amber-400/60 text-sm">
                <div className="text-amber-300 font-medium">{t("api.detail.pending_title")}</div>
                <div className="text-white/70 mt-1">{t("api.detail.pending_body")}</div>
              </div>
            )}

            <div className="mt-10">
              <h3 className="font-display text-lg text-white/85 mb-4">{t("api.detail.use_cases")}</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {((lang === "tr" && product.use_cases_tr) || product.use_cases)?.map((u) => (
                  <div key={u} className="lp-glass p-3 flex items-center gap-2 text-sm text-white/75">
                    <Check className="w-4 h-4 text-[#d4af37]" /> {u}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg text-white/85">{t("api.detail.endpoints")}</h3>
                <span className="text-xs text-white/50">{t("api.detail.base")}: <span className="font-mono text-white/70">{product.base_path}</span></span>
              </div>
              <div className="lp-card overflow-hidden">
                <table className="lp-table">
                  <thead>
                    <tr><th>{t("api.detail.method")}</th><th>{t("api.detail.path")}</th><th>{t("api.detail.description")}</th><th className="text-right">{t("api.detail.credits")}</th></tr>
                  </thead>
                  <tbody>
                    {product.endpoints?.map((e) => (
                      <tr key={e.path}>
                        <td><span className="lp-chip gold font-mono">{e.method}</span></td>
                        <td className="font-mono text-white/85">{e.path}</td>
                        <td className="text-white/60">{(lang === "tr" && e.description_tr) || e.description}</td>
                        <td className="text-right font-mono">{e.credits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-10">
              <h3 className="font-display text-lg text-white/85 mb-3">{t("api.detail.code_examples")}</h3>
              <div className="flex gap-2 mb-3">
                {["cURL", "JavaScript", "Python", "PHP"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setSnippetLang(l)}
                    className={`text-xs px-3 py-1.5 rounded-full font-mono ${snippetLang === l ? "bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37]" : "border border-white/10 text-white/60 hover:text-white"}`}
                    data-testid={`snippet-lang-${l.toLowerCase()}`}
                  >{l}</button>
                ))}
              </div>
              <CodeBlock code={snippet} language={snippetLang.toLowerCase()} testId={`snippet-${snippetLang.toLowerCase()}`} />
            </div>

            <div className="mt-10">
              <h3 className="font-display text-lg text-white/85 mb-3">{t("api.detail.sample_response")}</h3>
              <CodeBlock code={JSON.stringify(product.sample_response, null, 2)} language="json" testId="sample-response" />
            </div>
          </div>

          <div className="space-y-5 lg:sticky lg:top-24">
            {/* Playground */}
            <div className="lp-card p-6" data-testid="playground-card">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-white/90">{t("api.detail.playground.title")}</h3>
                <button
                  onClick={runPlayground}
                  disabled={pgLoading || product.coming_soon}
                  className={`lp-btn-primary text-sm py-2 px-4 ${product.coming_soon ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="playground-run">
                  {pgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                  {product.coming_soon ? t("marketplace.coming_soon") : (pgLoading ? t("api.detail.playground.running") : t("api.detail.playground.run"))}
                </button>
              </div>
              <p className="text-xs text-white/45 mt-2">
                {product.coming_soon
                  ? t("api.detail.playground_coming_soon")
                  : t("api.detail.playground.hint")}
              </p>
              <div className="mt-4 lp-terminal max-h-[280px] overflow-auto">
                <pre className="p-4 text-xs text-white/85 font-mono whitespace-pre-wrap">
                  {pgResponse ? JSON.stringify(pgResponse, null, 2) : `// ${t("api.detail.playground.click_run")}`}
                </pre>
              </div>
            </div>

            {/* Plans */}
            <div className="lp-card p-6" data-testid="plans-card">
              <h3 className="font-display text-white/90 mb-4">{t("api.detail.plans")}</h3>
              <div className="space-y-3">
                {product.plans?.filter((plan) => plan.tier !== "trial" && (plan.contact_sales || (plan.price_monthly || 0) > 0)).map((plan) => (
                  <div key={plan.id} className={`p-4 rounded-xl border ${plan.tier === "pro" ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-[#d4af37]/25 bg-white/2"}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/95 font-medium">{plan.display_name}</div>
                        <div className="text-xs text-white/50 mt-0.5">
                          {plan.monthly_requests.toLocaleString()} req · {plan.rate_limit_per_second} req/s
                        </div>
                      </div>
                      <div className="text-right">
                        {plan.contact_sales ? (
                          <div className="text-white/85 font-display">{t("api.detail.custom_price")}</div>
                        ) : (
                          <div>
                            <div className="font-display text-white/95 text-xl">
                              ${plan.price_monthly?.toFixed(0)}<span className="text-xs text-white/40 font-normal">/mo</span>
                            </div>
                            {plan.price_monthly > 0 && (
                              <div className="text-[10px] text-white/40 mt-0.5" data-testid={`plan-tax-${plan.tier}`}>
                                {t("pricing.tax.suffix")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => subscribe(plan)}
                      disabled={subLoading || product.coming_soon}
                      className={`w-full mt-3 justify-center ${plan.tier === "pro" ? "lp-btn-primary" : "lp-btn-outline-gold"} ${product.coming_soon ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`subscribe-${plan.tier}`}
                    >
                      {product.coming_soon
                        ? t("marketplace.coming_soon")
                        : plan.contact_sales
                          ? t("api.detail.contact_sales")
                          : subLoading
                            ? "…"
                            : t("common.subscribe")}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-white/45" data-testid="api-detail-tax-note">
                {t("pricing.tax.note")}
              </p>
            </div>

            <div className="lp-glass p-5 text-sm text-white/60">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-1">{t("api.detail.changelog_status")}</div>
              <div className="mt-1">
                {product.coming_soon
                  ? t("api.detail.coming_soon_title")
                  : product.maintenance
                    ? t("api.detail.maintenance")
                    : t("status.all_operational")}
              </div>
              <Link to="/status" className="text-[#d4af37] text-xs mt-1 inline-block">{t("api.detail.live_status")}</Link>
            </div>
          </div>
        </div>
      </section>

      {pendingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="subscribe-consent-modal">
          <div className="lp-card p-6 max-w-md w-full">
            <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("checkout.mor.notice_short") || t("checkout.mor.notice")}</div>
            <h3 className="font-display text-xl text-white/95 mb-2">
              {displayProductName((lang === "tr" && product.name_tr) || product.name)} · {pendingPlan.tier?.toUpperCase()}
            </h3>
            {pendingPlan.price_monthly > 0 && (
              <div className="mt-1 text-sm text-white/70" data-testid="modal-price-line">
                <span className="font-medium text-white/90">${pendingPlan.price_monthly?.toFixed(0)}/mo</span>
                <span className="text-white/50"> · {t("pricing.tax.suffix")}</span>
              </div>
            )}
            <p className="text-white/60 text-sm mt-2">
              {t("api.detail.confirm.body")}
            </p>
            <div className="mt-4 space-y-2 text-sm text-white/75">
              <label className="flex items-start gap-2">
                <input type="checkbox" checked={consentTerms} onChange={(e) => setConsentTerms(e.target.checked)} data-testid="sub-accept-terms" />
                <span>
                  {t("checkout.consent.terms.pre")}{" "}
                  <Link to="/legal/terms" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">{t("checkout.consent.terms.link")}</Link>{" "}
                  {t("checkout.consent.and")}{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">{t("checkout.consent.privacy.link")}</Link>.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input type="checkbox" checked={consentPrivacy} onChange={(e) => setConsentPrivacy(e.target.checked)} data-testid="sub-accept-privacy" />
                <span>{t("checkout.mor.notice")} <a href="https://www.creem.io/legal/buyer-terms" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">{t("checkout.mor.link")}</a>.</span>
              </label>
            </div>
            <div className="mt-6 flex items-center gap-3 justify-end">
              <button className="lp-btn-secondary" onClick={() => { setPendingPlan(null); setConsentTerms(false); setConsentPrivacy(false); }} data-testid="sub-cancel">
                {t("common.cancel")}
              </button>
              <button className="lp-btn-primary" disabled={subLoading || !consentTerms || !consentPrivacy} onClick={confirmSubscribe} data-testid="sub-confirm">
                {subLoading ? "…" : t("api.detail.confirm.cta")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
